// PO โมเดิร์นเทรด (เฟส A) — mirror ของ DTO ฝั่ง backend
// (src/modules/purchaseOrder/dto/response.ts และ dto/request.ts)
//
// ทุก endpoint ตอบด้วย envelope กลาง `{ statusCode, title, message, data, error }`
// จึงห่อด้วย `ApiResponse<T>` ที่ services/index.ts ไม่ต้องประกาศ wrapper รายตัว
//
// รูปแบบค่าที่ backend รับประกัน:
// - delivery_date / order_date เป็นวันที่ปฏิทินล้วน "YYYY-MM-DD" (ไม่มี timezone)
// - created_at / used_at เป็น Date ฝั่ง server → มาถึงเบราว์เซอร์เป็น ISO string
// - company_id เป็น string เสมอ (BIGINT ฝั่ง DB ส่งเป็น JSON number ไม่ได้)
// - amount ถูก coerce เป็น number แล้ว (pg คืน numeric เป็น string)

/** เชนที่ระบบรองรับ เฟสแรกนำเข้าได้เฉพาะ BIGC (LOTUS ถูกปฏิเสธ 422 ตอนนำเข้า) */
export type PurchaseOrderChain = "BIGC" | "LOTUS";

export const PURCHASE_ORDER_CHAIN_LABEL: Record<PurchaseOrderChain, string> = {
  BIGC: "บิ๊กซี",
  LOTUS: "โลตัส",
};

/**
 * 7 สถานะที่ใช้แสดงผล คำนวณตอนอ่านทุกครั้ง ไม่เก็บลง DB
 * ลำดับการตัดสิน (ข้อแรกที่ตรงชนะ): มีบิลที่ยังไม่ถูกยกเลิกถือ PO อยู่ → USED,
 * วันนี้ (Asia/Bangkok) เลยวันส่ง +1 → EXPIRED, ยังไม่มีสาขา → NO_BRANCH,
 * สาขาส่งคืนและ HO ยังไม่จัดการ → RETURNED, ยังไม่มีร้าน → UNMATCHED_CUSTOMER,
 * ยังไม่ส่งให้สาขา → PENDING_PUBLISH, นอกนั้น → READY
 *
 * NO_BRANCH เกิดจากการบันทึกไฟล์นำเข้าโดยที่ HO ยังไม่ได้ชี้สาขาให้บางแถว — HO ไประบุ
 * สาขาต่อที่รายการ PO (W1) ได้ และใบสถานะนี้ถูกข้ามตอนส่งให้สาขา
 *
 * RETURNED = สาขากด ส่งคืน HO พร้อมเหตุผล · ยังถือสาขาและร้านเดิมไว้ แต่ถูกดึงออกจาก
 * สาขาแล้ว (unpublished) รอ HO ย้ายสาขาหรือส่งกลับไปใหม่
 */
export type PurchaseOrderStatus =
  | "NO_BRANCH"
  | "RETURNED"
  | "UNMATCHED_CUSTOMER"
  | "PENDING_PUBLISH"
  | "READY"
  | "USED"
  | "EXPIRED";

/** คำไทยของสถานะ ใช้คำตามนี้ทั้ง BE label และ UI ตามที่ handbook กำหนด */
export const PURCHASE_ORDER_STATUS_LABEL: Record<
  PurchaseOrderStatus,
  string
> = {
  NO_BRANCH: "ยังไม่ระบุสาขา",
  RETURNED: "สาขาส่งคืน",
  UNMATCHED_CUSTOMER: "ยังไม่จับคู่ร้าน",
  PENDING_PUBLISH: "รอส่งสาขา",
  READY: "พร้อมใช้",
  USED: "ใช้แล้ว",
  EXPIRED: "หมดช่วงใช้",
};

/** ผลตรวจของ 1 แถวในไฟล์ที่อัปโหลด (จอ W2) */
export type PurchaseOrderImportRowResult =
  | "MATCHED"
  | "BRANCH_REQUIRED"
  | "DUPLICATE";

/**
 * หมวดตัวกรองแถวของ W2 ที่ไม่ใช่ผลตรวจจาก backend — แถวที่วันส่งเลยมาแล้ว
 * (past_delivery) ทับซ้อนกับผลตรวจได้ จึงเป็นหมวดของจอแยกต่างหาก
 */
export const PURCHASE_ORDER_IMPORT_PAST_DELIVERY_FILTER = "PAST_DELIVERY";
export const PURCHASE_ORDER_IMPORT_PAST_DELIVERY_LABEL = "เกินกำหนดส่ง";

/** คำไทยของผลตรวจ ตรงกับ 3 ใน 4 ค่าที่แถบสรุปของ W2 ต้องแสดง */
export const PURCHASE_ORDER_IMPORT_ROW_RESULT_LABEL: Record<
  PurchaseOrderImportRowResult,
  string
> = {
  MATCHED: "จับคู่ร้านได้",
  BRANCH_REQUIRED: "ต้องเลือกสาขา",
  DUPLICATE: "ซ้ำกับที่มีอยู่",
};

/** เหตุการณ์ในประวัติของ PO (จอ W6) */
export type PurchaseOrderEventType =
  | "IMPORTED"
  | "PUBLISHED"
  | "CUSTOMER_MATCHED"
  | "USED"
  | "RELEASED"
  | "RETURNED"
  | "BRANCH_CHANGED"
  | "UNPUBLISHED";

export interface PurchaseOrderBranchRef {
  company_id: string;
  branch_code: string;
  branch_name: string;
}

/**
 * ร้านที่ PO ผูกอยู่ — PO เก็บแค่ customer_uuid ส่วนรหัสกับชื่อ join จาก version
 * ปัจจุบันของลูกค้าตอนอ่าน ทั้งคู่ nullable เพราะคอลัมน์ต้นทางเป็น nullable
 */
export interface PurchaseOrderCustomerRef {
  customer_uuid: string;
  customer_code: string | null;
  customer_name: string | null;
}

/** แถวในไฟล์ที่ parser อ่านไม่ได้ — เป็นข้อมูลรายงาน ไม่ใช่ error ของทั้งไฟล์ */
export interface PurchaseOrderImportReportRow {
  /** `Sheet1!F373` — ชีต + เซลล์ ให้ผู้ใช้เปิดไฟล์ไปดูได้ตรงจุด */
  location: string;
  /** เลขแถวใน Excel เริ่มที่ 1 */
  row_no: number;
  cell_text: string;
  /** ข้อความไทยจาก parser แสดงตรง ๆ ได้เลย */
  reason: string;
}

/** ผลตรวจ 4 ค่าของ W2 รวมกันแล้วเท่ากับจำนวนแถวข้อมูลทั้งไฟล์ */
export interface PurchaseOrderImportPreviewSummary {
  matched: number;
  /** แถวที่ระบบระบุสาขาไม่ได้ — ไม่บล็อกการบันทึกแล้ว แถวที่ไม่ได้เลือกกลายเป็น NO_BRANCH */
  branch_required: number;
  duplicate: number;
  /** อ่านไม่ได้ — มี 1 รายการใน `skipped` ต่อ 1 แถว */
  unreadable: number;
  /** แถวที่วันส่งเลยมาแล้ว (ทับซ้อนกับสามค่าแรก) — ไม่นำเข้า เว้นแต่ HO ติ๊กนำเข้า */
  past_delivery: number;
}

export interface PurchaseOrderImportPreviewRow {
  /** เลขแถวใน Excel และเป็น key ที่ `branch_choices` อ้างกลับมา */
  row_no: number;
  po_number: string;
  delivery_date: string;
  order_date: string | null;
  /** รหัสร้านจากคอลัมน์ "สถานที่จัดส่ง" เติมศูนย์หน้าให้ครบ 5 หลักแล้ว */
  ship_to_code: string;
  /** ชื่อดิบตามที่สะกดในไฟล์ แสดงคู่กับร้านที่จับคู่ได้ */
  ship_to_name: string;
  amount: number | null;
  result: PurchaseOrderImportRowResult;
  /**
   * วันส่งเลยมาแล้ว · ถูกข้ามตอนยืนยัน เว้นแต่อยู่ใน include_past_row_nos และใบที่นำเข้า
   * จะเป็น หมดช่วงใช้ ทันที
   */
  past_delivery: boolean;
  /** null เสมอ ยกเว้น result เป็น MATCHED */
  customer: PurchaseOrderCustomerRef | null;
  /** null เสมอ ยกเว้น result เป็น MATCHED */
  branch: PurchaseOrderBranchRef | null;
  /**
   * สาขาที่มีร้านถือ store number นี้ ใส่มาเฉพาะแถว BRANCH_REQUIRED และ W2 ต้องเสนอ
   * ไว้บนสุดของ dropdown · ว่าง = ไม่มีร้านไหนถือรหัสนี้เลย ให้เลือกจากสาขาทั้งหมด
   */
  branch_candidates: PurchaseOrderBranchRef[];
}

/**
 * ผลตรวจไฟล์ของ W2 — ยังไม่เขียน DB ระบบพักแผนไว้ใต้ `upload_id` และ confirm
 * เท่านั้นที่เขียนจริง · `rows` มีครบทุกแถวที่อ่านได้ (ไฟล์จริง ~8,700 แถว)
 * จอจึงต้องกรองและแบ่งหน้าฝั่ง client ไม่ใช่อัปไฟล์ใหม่เพื่อเปลี่ยนมุมมอง
 */
export interface PurchaseOrderImportPreviewData {
  upload_id: string;
  /** อายุของผลตรวจเป็นวินาที (30 นาที) หมดแล้วต้องอัปไฟล์ใหม่ */
  expires_in_seconds: number;
  file_name: string;
  chain: PurchaseOrderChain;
  summary: PurchaseOrderImportPreviewSummary;
  rows: PurchaseOrderImportPreviewRow[];
  skipped: PurchaseOrderImportReportRow[];
}

export interface PurchaseOrderImportConfirmData {
  batch_uuid: string;
  chain: PurchaseOrderChain;
  file_name: string;
  /** แถวข้อมูลที่ parser อ่านได้ทั้งหมด */
  row_count: number;
  /** แถวที่เขียนเป็น PO จริง = N ในปุ่ม "ส่งให้สาขาทั้ง N ใบ" ของขั้น 3 */
  saved_count: number;
  /** แถวที่ข้ามเพราะ (chain, po_number) ซ้ำกับที่มีอยู่ */
  skipped_count: number;
  /** ในจำนวนที่บันทึก มีกี่ใบที่ยังไม่มีร้าน */
  unmatched_count: number;
  /** ในจำนวนที่บันทึก มีกี่ใบที่ยังไม่ระบุสาขา (แถว ต้องเลือกสาขา ที่ไม่ได้เลือก) */
  no_branch_count: number;
  /** แถวที่เกินกำหนดส่งและไม่ได้ติ๊กนำเข้า จึงไม่ถูกบันทึก */
  past_delivery_skipped_count: number;
}

/** เหตุผลที่สาขาส่งคืน PO — แสดงที่แถวและ drawer ของ W1 */
export interface PurchaseOrderReturnInfo {
  reason: string;
  /** ISO string */
  returned_at: string;
  returned_by_name: string | null;
}

/** 1 แถวของรายการ PO (W1 ของ HO, W7 ของสาขา) และของไฟล์ Export */
export interface PurchaseOrderListItem {
  uuid: string;
  chain: PurchaseOrderChain;
  po_number: string;
  delivery_date: string;
  order_date: string | null;
  ship_to_code: string;
  ship_to_name: string | null;
  amount: number | null;
  status: PurchaseOrderStatus;
  /** null ระหว่างที่ PO ยังเป็น ยังไม่ระบุสาขา (NO_BRANCH) */
  branch: PurchaseOrderBranchRef | null;
  /** null ระหว่างที่ PO ยังเป็น ยังไม่จับคู่ร้าน */
  customer: PurchaseOrderCustomerRef | null;
  /** สาย — อ่านจากสายหลักของร้านตอนแสดงผล ไม่เก็บบน PO จึงแก้จากจอ PO ไม่ได้ */
  route_number: string | null;
  /** มีค่าเฉพาะเมื่อ status เป็น RETURNED */
  return_info: PurchaseOrderReturnInfo | null;
  /** ISO string ของเวลาที่ส่งให้สาขา · null = ยังไม่ส่ง (หรือถูกดึงกลับ/ส่งคืนแล้ว) */
  published_at: string | null;
}

/** แถบสรุปสถานะของ W1 — นับทั้งชุดที่ตัวกรองครอบคลุม ไม่ใช่เฉพาะหน้าปัจจุบัน */
export type PurchaseOrderStatusSummary = Record<PurchaseOrderStatus, number>;

export interface PurchaseOrderListData {
  rows: PurchaseOrderListItem[];
  count: number;
  summary: PurchaseOrderStatusSummary;
}

/** ไฟล์นำเข้าต้นทางของ PO ใบนี้ ระบุใน W6 เพื่อตามรอยไฟล์ที่นำเข้าผิดได้ */
export interface PurchaseOrderImportBatchRef {
  uuid: string;
  file_name: string;
  /** ISO string */
  created_at: string;
}

/** รายละเอียดเฉพาะของแต่ละเหตุการณ์ แต่ละฟิลด์เป็นของ type ที่ระบุไว้ */
export interface PurchaseOrderEventPayload {
  /** CUSTOMER_MATCHED — รหัสร้านจากไฟล์ */
  ship_to_code?: string;
  /** CUSTOMER_MATCHED — ร้านที่คนเลือก */
  customer_code?: string;
  /** CUSTOMER_MATCHED — ชื่อร้าน ณ ตอนที่เลือก */
  customer_name?: string;
  /** USED / RELEASED — เลขบิลที่ใช้หรือคืน PO */
  doc_no?: string;
  /** RETURNED — เหตุผลที่สาขาส่งคืน */
  reason?: string;
  /** RETURNED — สาขาที่ส่งคืน · BRANCH_CHANGED — สาขาใหม่ */
  company_id?: string;
  branch_code?: string;
  /** BRANCH_CHANGED — สาขาเดิม (ว่างเมื่อเดิมยังไม่ระบุสาขา) */
  from_company_id?: string | null;
  from_branch_code?: string | null;
}

export interface PurchaseOrderEvent {
  uuid: string;
  type: PurchaseOrderEventType;
  /** Keycloak subject ของผู้ทำ */
  actor_user_id: string;
  /** ชื่อที่ flow ผู้เขียน event เก็บไว้ · null เมื่อไม่มีชื่อให้เก็บ */
  actor_name: string | null;
  payload: PurchaseOrderEventPayload | null;
  /** ISO string */
  created_at: string;
}

/** บิลที่ถือ PO ใบนี้อยู่ */
export interface PurchaseOrderUsedBy {
  doc_no: string;
  /** ISO string */
  used_at: string;
  sales_name: string | null;
}

/** W6 drawer อ่านอย่างเดียว: แถวในรายการ + ที่มา + สิ่งที่เกิดขึ้นกับใบนี้ */
export interface PurchaseOrderDetail extends PurchaseOrderListItem {
  import_batch: PurchaseOrderImportBatchRef;
  /** null เสมอ ยกเว้น status เป็น USED */
  used_by: PurchaseOrderUsedBy | null;
  events: PurchaseOrderEvent[];
}

export interface PurchaseOrderPublishBranchSummary {
  branch: PurchaseOrderBranchRef;
  published_count: number;
  /** ในจำนวนที่ส่งไป มีกี่ใบที่ไปถึงสาขาโดยยังไม่มีร้าน */
  unmatched_count: number;
}

/**
 * ผลของ ส่งให้สาขา — ไม่เคยปฏิเสธ ใบที่ยังไม่จับคู่ร้านก็ถูกส่งไปด้วย ส่วนใบที่ยังไม่ระบุ
 * สาขาถูกข้าม (ไม่ใช่ error) และนับไว้ใน skipped_no_branch_count
 */
export interface PurchaseOrderPublishData {
  published_count: number;
  unmatched_count: number;
  skipped_no_branch_count: number;
  branches: PurchaseOrderPublishBranchSummary[];
}

/** 1 ตัวเลือกใน dropdown จับคู่ร้านของ W7 */
export interface PurchaseOrderCustomerCandidate {
  customer_uuid: string;
  customer_code: string | null;
  customer_name: string | null;
  /** แสดงไว้ให้แยกร้านที่ชื่อคล้ายกันออกจากกัน */
  store_number: string | null;
}

export interface PurchaseOrderCustomerNameMatch
  extends PurchaseOrderCustomerCandidate {
  /** ความคล้ายของชื่อ 0-100 แสดงเป็น "92%" */
  similarity_percent: number;
}

/**
 * 3 กลุ่มของ dropdown W7 ตามลำดับที่ต้องเสนอ · ทุกกลุ่มถูกจำกัดไว้เฉพาะสาขาของ PO
 * และ prefix ของเชนแล้วจาก backend
 */
export interface PurchaseOrderCustomerCandidatesData {
  /** (ก) "store number ตรงกัน" */
  store_number_matches: PurchaseOrderCustomerCandidate[];
  /** (ข) ร้านชื่อคล้าย */
  name_matches: PurchaseOrderCustomerNameMatch[];
  /** (ค) ค้นหาเอง — ว่างเมื่อไม่ได้ส่ง q */
  search: PurchaseOrderCustomerCandidate[];
}

// ---- Request ----

/** สาขาที่ HO เลือกให้แถวที่ระบบระบุสาขาไม่ได้ (ขั้นยืนยันของ W2) */
export interface PurchaseOrderImportBranchChoice {
  row_no: number;
  company_id: string;
}

/**
 * ตัวกรองของรายการ PO ใช้ร่วมกันระหว่างตารางกับ Export ไฟล์จึงเป็นสิ่งเดียวกับ
 * ที่เห็นบนจอเสมอ · Export ไม่รับ limit/page จึงแยก paging ออกไปไว้ที่
 * PurchaseOrderListParams
 */
export interface PurchaseOrderListFilter {
  /** "YYYY-MM-DD" */
  delivery_from?: string;
  /** "YYYY-MM-DD" ต้องไม่น้อยกว่า delivery_from ไม่อย่างนั้น backend ตอบ 400 */
  delivery_to?: string;
  /**
   * เลือกสาขาได้เฉพาะ HO · ผู้ใช้สาขาถูก backend ปักไว้ที่สาขาตัวเองอยู่แล้ว
   * `"none"` (PURCHASE_ORDER_NO_BRANCH_FILTER) = เฉพาะใบที่ยังไม่ระบุสาขา
   */
  company_id?: string;
  chain?: PurchaseOrderChain;
  status?: PurchaseOrderStatus;
  /** ค้นหาเลข PO / รหัสร้าน / ชื่อร้าน */
  q?: string;
  /** ดูเฉพาะ PO ที่มาจากไฟล์นำเข้าครั้งนั้น */
  batch_uuid?: string;
  /** สายของร้าน · `"none"` (PURCHASE_ORDER_NO_ROUTE_FILTER) = ใบที่ยังไม่ผูกร้าน */
  route_number?: string;
}

export interface PurchaseOrderListParams extends PurchaseOrderListFilter {
  /** เริ่มที่ 1 */
  page: number;
  limit: number;
}

/** ส่งให้สาขา — ต้องมี uuids หรือ batch_uuid อย่างใดอย่างหนึ่งเท่านั้น */
export interface PurchaseOrderPublishRequest {
  uuids?: string[];
  batch_uuid?: string;
}

/** ค่า company_id ของตัวกรองที่แปลว่า "ใบที่ยังไม่ระบุสาขา" (HO เท่านั้น) */
export const PURCHASE_ORDER_NO_BRANCH_FILTER = "none";

/** ค่า route_number ของตัวกรองที่แปลว่า "ใบที่ยังไม่ผูกร้าน" (จึงไม่มีสาย) */
export const PURCHASE_ORDER_NO_ROUTE_FILTER = "none";

/**
 * ระบุ/ย้ายสาขา (HO เท่านั้น) · รับทั้งใบที่ยังไม่ส่งและใบที่ส่งให้สาขาแล้ว — ย้ายใบที่
 * ส่งไปแล้วเท่ากับดึงกลับจากสาขาเดิม · 409 ถ้าในรายการมีใบที่ใช้แล้วหรือหมดช่วงใช้ ·
 * 404 ถ้าไม่รู้จักสาขา
 */
export interface PurchaseOrderSetBranchRequest {
  /** สูงสุด 500 ใบต่อครั้ง */
  uuids: string[];
  company_id: string;
  /** ขยายไปยังใบอื่นที่ยังไม่ระบุสาขาและยังไม่ใช้ ของเชน + store number เดียวกัน */
  include_same_store?: boolean;
  /** ระบุสาขาแล้วส่งให้สาขาเลยในคำขอเดียว (บันทึกและส่งสาขา) */
  publish?: boolean;
}

export interface PurchaseOrderSetBranchData {
  /** ใบที่ถูกระบุสาขาทั้งหมด รวมใบที่ขยายมาจาก store number เดียวกัน */
  updated_count: number;
  /** ในจำนวนที่ระบุสาขา มีกี่ใบที่ระบบผูกร้านให้ได้ทันที */
  matched_count: number;
  /** ใบที่ถูกส่งให้สาขา (0 เมื่อไม่ได้ส่ง publish) */
  published_count: number;
  rows: PurchaseOrderListItem[];
}

/** ส่งคืน HO (ผู้ใช้สาขาเท่านั้น) · เฉพาะใบที่ส่งมาแล้ว ยังไม่ใช้ และยังไม่หมดช่วงใช้ */
export interface PurchaseOrderReturnRequest {
  uuids: string[];
  /** 1..500 ตัวอักษร บังคับ */
  reason: string;
}

/** ดึงกลับจากสาขา (HO เท่านั้น) · 409 ถ้ามีใบที่ใช้แล้ว */
export interface PurchaseOrderUnpublishRequest {
  uuids: string[];
}

export interface PurchaseOrderUnpublishData {
  unpublished_count: number;
}

export interface PurchaseOrderReturnData {
  returned_count: number;
}

// ---- สรุปรายสัปดาห์ของ W7: สาย × วันส่ง ----

export interface PurchaseOrderWeeklySummaryParams {
  /** "YYYY-MM-DD" ต้องเป็นวันจันทร์ */
  week_start: string;
  /** HO เท่านั้น · ผู้ใช้สาขาถูก backend ปักไว้ที่สาขาตัวเอง */
  company_id?: string;
  chain?: PurchaseOrderChain;
}

export interface PurchaseOrderWeeklyCell {
  /** "YYYY-MM-DD" = วันส่งของ PO */
  date: string;
  po_count: number;
  /** จำนวนร้านไม่ซ้ำในช่องนี้ */
  customer_count: number;
}

export interface PurchaseOrderWeeklyRouteCell extends PurchaseOrderWeeklyCell {
  status_counts: Record<PurchaseOrderStatus, number>;
}

export interface PurchaseOrderWeeklyRoute {
  /** null = ใบที่ยังไม่จับคู่ร้าน จึงยังไม่มีสาย */
  route_number: string | null;
  /** 7 ช่อง จันทร์..อาทิตย์ ตามลำดับเดียวกับ `days` */
  cells: PurchaseOrderWeeklyRouteCell[];
  po_count: number;
  customer_count: number;
}

/** ไม่รวมใบที่ยังไม่ระบุสาขา (NO_BRANCH) */
export interface PurchaseOrderWeeklySummaryData {
  week_start: string;
  /** 7 วัน จันทร์..อาทิตย์ "YYYY-MM-DD" */
  days: string[];
  routes: PurchaseOrderWeeklyRoute[];
  totals: {
    cells: PurchaseOrderWeeklyCell[];
    po_count: number;
    customer_count: number;
  };
}
